import Button from 'components/Form/Button';
import TextField from 'components/Form/TextField';
import React, { useEffect, useState } from 'react';
import CustomSelect from 'components/Form/CustomSelect';
import { formatToMoney } from 'utils';
import {
  useGetAPlan,
  useInitializePaymentMutation
} from 'common/queries-and-mutations/payment';
import Paystack from 'components/Paystack';
import { toast } from 'react-toastify';
import { Alert } from 'components/Toast';
import { PaystackSuccessDto } from 'service/dto/payment.dto';
import { Loader } from 'components/Loader';

interface IProps {
  onSubmit: (ars: any) => void;
  formValue: {
    surname: string;
    otherName: string;
    email: string;
    planId: number | null;
    planAmount: number | null;
  };
  isProcessing: boolean;
  isRegisterError: boolean;
}

export default function Payment({
  onSubmit,
  formValue,
  isProcessing,
  isRegisterError
}: IProps) {
  const [showPaystack, setShowPaystack] = useState(false);

  const { data: selectedPlan, isFetching } = useGetAPlan({
    planId: formValue.planId!
  });

  const { mutate, isLoading, isSuccess, data } = useInitializePaymentMutation();

  const initializePayment = () => {
    const payload = {
      full_name: `${formValue.surname} ${formValue.otherName}`,
      email: formValue.email,
      amount: formValue.planAmount,
      description: `Payment for iWill with plan ID: ${formValue.planId} with total amount of ${formValue.planAmount}`,
      planId: formValue.planId
    };
    mutate(payload);
  };

  useEffect(() => {
    if (isSuccess && data) {
      setShowPaystack(prevState => !prevState);
    }
  }, [data, isSuccess]);

  const onPaymentClose = () => {
    setShowPaystack(false);
    toast.error(<Alert type={'error'} message={'Payment close'} />);
  };

  const onPaymentSuccess = (paystackSuccessDto: PaystackSuccessDto) => {
    if (
      paystackSuccessDto.message === 'Approved' &&
      paystackSuccessDto.status === 'success'
    ) {
      //Create user here
      onSubmit(paystackSuccessDto.reference);
    }
  };

  if (isFetching) {
    return (
      <div className="mt-40 flex flex-col items-center justify-center">
        <Loader color={'#FF6606'} height={50} width={50} />
      </div>
    );
  }

  return (
    <div>
      <div className="mx-32 my-10 flex flex-col items-center justify-center">
        <div className="w-full rounded-[10px] border-2 border-[#E2E5EE]">
          <div className="border-b p-6 text-center">
            <p>Quick One</p>
          </div>

          <div className="flex flex-col items-center justify-center pb-8">
            <div className="w-[35%] py-9 text-center">
              <div className="text-lg">
                <span>Kindly make the payment of </span>
                <span className="font-bold text-[#FF6600]">
                  {formatToMoney(selectedPlan?.price)}
                </span>
                <span> to continue with Your Will</span>
              </div>
            </div>
            <div className="mb-10 w-[45%] rounded-md border border-[#E2E5EE] bg-[#F0F0F0]">
              <div className="grid p-5 md:grid-cols-2 md:gap-2">
                <div className="group relative z-0 w-full">
                  <TextField
                    name="firstName"
                    placeholder=""
                    value={formValue.surname}
                    label="First Name"
                    size="sm"
                    className="rounded border-[1.5px] border-gray-300"
                  />
                </div>
                <div className="group relative z-0 w-full">
                  <TextField
                    name="lastName"
                    placeholder=""
                    value={formValue.otherName}
                    label="Last Name"
                    size="sm"
                    className="rounded border-[1.5px] border-gray-300"
                  />
                </div>
              </div>
              <div className="relative z-0 w-full px-5 pb-5">
                <TextField
                  name="emailAddress"
                  placeholder=""
                  value={formValue.email}
                  label="Email Address"
                  size="sm"
                  className="rounded border-[1.5px] border-gray-300 text-sm"
                />
              </div>
              <div className="mx-2.5 grid p-3 md:grid-cols-2 md:gap-2">
                <div className="group relative z-0">
                  <CustomSelect
                    options={[{ value: 'NGN', label: 'NGN' }]}
                    selectedOption={{ value: 'NGN', label: 'NGN' }}
                    handleOptionChange={() => {}}
                    label="Amount to charge"
                    className="mt-1.5 rounded text-sm"
                    useDefaultDropdownIcon
                  />
                </div>
                <div className="group relative z-0 mt-8">
                  <TextField
                    name="amount"
                    placeholder=""
                    value={formatToMoney(selectedPlan?.price)}
                    label=""
                    size="sm"
                    className="h-[2.4rem] rounded border-[1.5px] border-gray-300 "
                  />
                </div>
              </div>
              {/* This should only happen is registration fail due to unknown error */}
              {isRegisterError && (
                <Button
                  variant="primary"
                  size="md"
                  disabled={isProcessing}
                  loading={isProcessing}
                  loadingText={'Please Wait...'}
                  className={`mb-3 ${
                    isProcessing
                      ? 'w-[13rem] p-[0.5rem]'
                      : 'w-[7rem] p-[0.1rem]'
                  } rounded bg-emerald-700`}
                  label="Try again"
                  onClick={() => onSubmit(data?.reference)}
                ></Button>
              )}
              <div className="my-5 flex items-center justify-center">
                {showPaystack && data ? (
                  <Paystack
                    isProcessing={isProcessing}
                    onPaymentSuccess={onPaymentSuccess}
                    onPaymentClose={onPaymentClose}
                    reference={data.reference}
                    amount={selectedPlan?.price ?? formValue.planAmount!}
                    email={formValue.email}
                  />
                ) : (
                  <Button
                    variant="primary"
                    size="md"
                    disabled={isLoading}
                    loading={isLoading}
                    loadingText={'Please Wait...'}
                    className={`mb-3 ${
                      isLoading ? 'w-[13rem] p-[0.5rem]' : 'w-[7rem] p-[0.1rem]'
                    } rounded bg-emerald-700`}
                    label="Pay Now"
                    onClick={initializePayment}
                  ></Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
